<template>
  <div>
    <v-container>
      <AppFilterNavigation
        ref="filters"
        :filters.sync="filters"
        mobile-label-key="navigation:calendarFilters.Mobile.heading"
        :filter-name="
          showNoncompetitions ? 'educationCalendarFilters' : 'calendarFilters'
        "
        use-local-storage
        style="z-index: 11"
      >
        <template #default>
          <v-col
            v-if="!showNoncompetitions"
            cols="12"
            md="2"
            class="px-1 mb-2 text-center"
          >
            <AppSelectDiscipline
              v-model="filters.disciplinesSelected"
              data-test="disciplines"
              :menu-props="customMenuOptions"
              multiple
              is-filter
            />
          </v-col>

          <v-col
            v-if="!showNoncompetitions"
            cols="12"
            md="2"
            class="px-1 mb-2 text-center"
          >
            <AppSelectCompetitionLevels
              v-model="filters.competitionLevelsSelected"
              data-test="competitionLevels"
              :label="
                $t(
                  'calendar:filters.CompetitionLevels.label',
                  'Competition levels',
                  'Label of the Competition levels filter'
                )
              "
              :menu-props="customMenuOptions"
              multiple
              is-filter
              hide-details
              clearable
            />
          </v-col>

          <v-col
            cols="12"
            md="2"
            class="px-1 mb-2 text-center hide-menu-element"
            style="position: relative"
          >
            <AppSelectMultipleDates
              :date-from.sync="eventsFrom"
              :date-until.sync="eventsUntil"
              data-test="dates"
              :date-options="dateOptions"
              :default-date-from="[0, 'days']"
              is-filter
              :label="
                $t(
                  'calendar:filters.time.label',
                  'Time',
                  'Label of the time filter'
                )
              "
            />
          </v-col>

          <v-col
            v-if="showNoncompetitions"
            cols="12"
            md="2"
            class="px-1 mb-2 text-center"
          >
            <AppSelectEventCategory
              v-model="filters.eventCategoryIds"
              data-test="eventCategoryIds"
              multiple
              hide-details
              clearable
              filter-input
              :label="
                $t('calendar:filters.EventCategories.label', 'Categories')
              "
            />
          </v-col>

          <v-col cols="12" md="2" class="px-1 mb-2 text-center">
            <AppSelectRegions
              v-model="filters.regionsSelected"
              data-test="regions"
              :label="
                $t(
                  'calendar:filters.Regions.label',
                  'Regions',
                  'Label of the regions filter'
                )
              "
              item-value="id"
              item-text="name"
              :menu-props="customMenuOptions"
              multiple
              clearable
              is-filter
              include-disabled
            />
          </v-col>

          <v-col
            v-if="showNoncompetitions"
            cols="12"
            md="2"
            class="px-1 mb-2 text-center"
          >
            <AppSelectOrganisations
              v-model="filters.organisersSelected"
              :label="
                $t(
                  'calendar:filters.Organisers.label',
                  'Organisers',
                  'Label of the Organisers filter'
                )
              "
              :menu-props="customMenuOptions"
              is-filter
              multiple
              tooltip
              clearable
            />
          </v-col>

          <v-col
            v-if="$vuetify.breakpoint.mdAndUp"
            cols="12"
            md="4"
            class="px-1 mb-2 text-center"
          >
            <AppSearchBox :value.sync="filters.search" clearable />
          </v-col>
        </template>
        <template #advanced="{ showCheckboxes }">
          <v-col cols="12" md="3">
            <div class="px-1 mb-2 text-center">
              <AppSelectCountries
                v-model="filters.countryIds"
                :label="$t('calendar:filters.country.label', 'Countries')"
                :input-props="{
                  multiple: true,
                  hideDetails: true,
                  clearable: true
                }"
                is-filter
              />
            </div>

            <div class="px-1 mb-2 text-center">
              <AppSelectPlace
                v-model="filters.eventPlaceFilterSelected"
                data-test="place"
                :menu-props="customMenuOptions"
                clearable
                tooltip
                is-filter
              />
            </div>

            <div v-if="!showNoncompetitions" class="px-1 mb-2 text-center">
              <AppSelectOrganisations
                v-model="filters.organisersSelected"
                :label="
                  $t(
                    'calendar:filters.Organisers.label',
                    'Organisers',
                    'Label of the Organisers filter'
                  )
                "
                :menu-props="customMenuOptions"
                is-filter
                multiple
                tooltip
                clearable
              />
            </div>
          </v-col>

          <v-col cols="12" md="3">
            <div v-if="!showNoncompetitions" class="px-1 mb-2 text-center">
              <AppGenericInput
                v-model="filters.findEventOptions"
                :component-name="
                  showCheckboxes ? 'app-select-checkbox' : 'v-select'
                "
                :items="findEventOptions"
                data-test="findEventOptions"
                :label="$t('calendar:filters.findEvent.label', 'Find event')"
                filter-input
                multiple
                :menu-props="customMenuOptions"
                clearable
              />
            </div>

            <div v-if="showNoncompetitions" class="px-1 mb-2 text-center">
              <AppSelectProfQualification
                v-model="filters.profQualificationIds"
                :label="
                  $t(
                    'calendar:filters.eventProfessionalQualifications.label',
                    'Professions'
                  )
                "
                :input-props="{
                  filterInput: true,
                  multiple: true,
                  clearable: true,
                  dataTest: 'profQualificationIds'
                }"
              />
            </div>
            <div v-if="!showNoncompetitions" class="px-1 mb-2 text-center">
              <AppSelectCups
                v-model="filters.cupIds"
                :discipline-ids="filters.disciplinesSelected"
                multiple
                data-test="cups"
                :menu-props="customMenuOptions"
                clearable
                is-filter
              />
            </div>

            <div v-if="!showNoncompetitions" class="px-1 mb-2 text-center">
              <AppGenericInput
                v-model="filters.hasEntriesOptions"
                :component-name="
                  showCheckboxes ? 'app-select-checkbox' : 'v-select'
                "
                :items="hasEntriesOptions"
                data-test="hasEntriesOptions"
                :label="$t('calendar:filters.hasEntries.label', 'Has entries')"
                filter-input
                multiple
                :menu-props="customMenuOptions"
                clearable
              />
            </div>
          </v-col>

          <v-col cols="12" md="3">
            <div class="px-1 mb-2 text-center">
              <AppGenericInput
                v-model="filters.findQuicklyOptions"
                :component-name="
                  showCheckboxes ? 'app-select-checkbox' : 'v-select'
                "
                :items="findQuicklyOptions"
                data-test="findQuicklyOptions"
                :label="
                  $t('calendar:filters.findQuickly.label', 'Find quickly')
                "
                filter-input
                multiple
                :menu-props="customMenuOptions"
                clearable
              />
            </div>

            <div style="position: relative" class="px-1 mb-2 text-center">
              <AppGenericSelect
                v-model="filters.statusesSelected"
                :items="statusesFilterOptions"
                data-test="statuses"
                :label="
                  $t(
                    'calendar:filters.Status.label',
                    'Status',
                    'Label of the event status filter'
                  )
                "
                use-item-color
                item-value="value"
                item-text="text"
                is-filter
                :menu-props="customMenuOptions"
                clearable
                multiple
              />
            </div>
            <!--
            <div class="px-1 mb-2 text-center">
              <AppGenericSelect
                v-model="filters.walletStatuses"
                :items="walletStatuses"
                data-test="walletStatus"
                :label="$t('calendar:filters.WalletStatus.label', 'Payments')"
                is-filter
                box-outlined
                :menu-props="customMenuOptions"
                clearable
              />
            </div>
            <div v-if="!showNoncompetitions" class="px-1 mb-2 text-center">
              <AppGenericSelect
                v-model="filters.useEquipe"
                :items="[
                  {
                    value: true,
                    text: $t('calendar:filters.Equipe.open.label', 'Used')
                  },
                  {
                    value: false,
                    text: $t(
                      'calendar:filters.WalletStatus.Equipe.NotUsed.label',
                      'Not used'
                    )
                  }
                ]"
                data-test="walletStatus"
                :label="$t('calendar:filters.Equipe.label', 'Equipe')"
                is-filter
                box-outlined
                :menu-props="customMenuOptions"
                clearable
              />
            </div>
          -->
          </v-col>

          <v-col
            v-if="$auth.canAccessRoute('CalendarAdminView')"
            cols="12"
            md="3"
          >
            <div class="px-1 mb-2 text-center">
              <AppSelectRoles
                v-model="filters.noPeopleAssignedInRoleIds"
                multiple
                :have-scopes="['RoleInEvent']"
                :label="
                  $t(
                    'calendar:filters.missingStaffForRole.label',
                    'Missing staff for role'
                  )
                "
                is-filter
                clearable
              />
            </div>

            <div class="px-1 mb-2 text-center">
              <AppSelectPeople
                v-model="filters.personHavingRoleInEventId"
                :label="
                  $t(
                    'calendar:filters.eventsWithAssignedStaffMember.label',
                    'Staff member in events'
                  )
                "
                is-filter
              />
            </div>
          </v-col>
        </template>

        <template #mobile>
          <AppSearchBox :value.sync="filters.search" class="px-4" clearable />
        </template>
      </AppFilterNavigation>

      <div
        data-test="calendarmodes"
        class="d-flex my-auto justify-end pa-3 flex-wrap d-print-none"
      >
        <v-btn
          v-if="!showNoncompetitions"
          text
          color="primary"
          data-test="month-view-btn"
          large
          @click="monthCalendarView"
        >
          <v-icon left color="primary">$iconCalendar</v-icon>
          {{ $t("calendar:settings.monthView", "Month") }}
        </v-btn>
        <v-btn text color="primary" large @click="ganttCalendarView">
          <v-icon left color="primary">$iconChartBars</v-icon>
          {{ $t("calendar:settings.GanttView", "Gantt") }}
        </v-btn>
        <v-btn text color="primary" large @click="compactOrNormalView">
          <v-icon left>$iconImage</v-icon>
          {{
            calendarView == "normalView"
              ? $t(
                  "calendar:settings.TableView",
                  "Table view",
                  "Show calendar as table"
                )
              : $t(
                  "calendar:settings.CardView",
                  "Card view",
                  "Show calendar as cards"
                )
          }}
        </v-btn>
        <template v-if="$auth.canAccessRoute('CalendarAdminView')">
          <v-btn
            v-if="calendarView == 'adminView'"
            text
            large
            color="primary"
            @click="() => changeBreakdownMode()"
            ><v-icon left>fa-light fa-list-check</v-icon>
            <template v-if="breakdownMode == 'week'">{{
              $t(
                "calendar:settings.adminViewSwitchToMonthly",
                "Administrative monthly"
              )
            }}</template
            ><template v-else>{{
              $t(
                "calendar:settings.adminViewSwitchToWeekly",
                "Administrative weekly"
              )
            }}</template></v-btn
          >

          <v-btn v-else text color="primary" large @click="adminCalendarView">
            <v-icon left>fa-light fa-list-check</v-icon>
            {{ $t("calendar:settings.adminView", "Administrative") }}
          </v-btn>
        </template>

        <template v-if="showNoncompetitions">
          <v-btn
            v-if="$auth.canAccessRoute('NoncompetitionEventCreationPage')"
            :to="{ name: 'NoncompetitionEventCreationPage' }"
            data-test="courseForm"
            large
            color="edit"
            text
          >
            <b>
              <v-icon class="mr-1" color="edit">$edit</v-icon>
              {{ $t("events:button.addNonCompetition", "Add new") }}
            </b>
          </v-btn>
        </template>
        <template v-else>
          <v-btn
            v-if="$auth.canAccessRoute('EventCreationPage')"
            :to="{ name: 'EventCreationPage' }"
            data-test="eventForm"
            large
            color="edit"
            text
          >
            <b>
              <v-icon class="mr-1" color="edit">$edit</v-icon>
              {{ $t("events:button.addEvent", "Event") }}
            </b>
          </v-btn>
        </template>
        <slot name="event-export" />
      </div>

      <div
        class="pa-5 pt-2"
        :class="{
          'd-none': !loadings.eventLoading
        }"
      >
        <v-progress-linear
          :active="loadings.eventLoading"
          indeterminate
          height="8"
        />
      </div>

      <template v-if="calendarView == 'monthView'">
        <CalendarMonth
          :filters="queryVariables"
          :events-from="eventsFrom"
          :events-until="eventsUntil"
        />
      </template>
      <template v-else-if="calendarView == 'adminView'">
        <CalendarAdmin
          :filters="queryVariables"
          :breakdown-mode="breakdownMode"
        />
      </template>
      <template v-else-if="calendarView == 'compactView'">
        <v-card color="primary" class="fipa--header d-print-none" tile />
        <EventsTemplateEvents
          :items="events"
          show-ads
          map-modal
          @placeDetails="placeDetails($event)"
        />
      </template>
      <template v-else-if="calendarView == 'ganttView'">
        <AppGantt
          :events="events"
          :date-from="eventsFrom"
          :loading="loadings.eventLoading"
          :date-until="eventsUntil"
          @loadMore="$emit('loadMore')"
        />
      </template>
      <template v-else>
        <v-data-iterator
          ref="eventCards"
          :items="events"
          :loading="loadings.eventLoading"
          :items-per-page="-1"
          hide-default-footer
        >
          <template #no-data>
            <h2 class="secondary--text text-center mb-8">
              {{ $t("events:noEventsFound", "No events found") }}
            </h2>
          </template>

          <template #loading>
            <EventCardPlaceholder data-test="placeholder-event" />
          </template>

          <template #default="{ items }">
            <div v-for="(item, index) in items" :key="item.id" class="mb-8">
              <div v-if="showBannerConditionally(index)">
                <AppShowBanners
                  placement-name="CALENDAR"
                  :show-only-positions="bannerPositionNumbers"
                />
              </div>
              <div :data-scroll-focus="item.id">
                <EventCard
                  type="EventCard"
                  :event="item"
                  map-modal
                  @placeDetails="placeDetails($event)"
                />
              </div>
            </div>
          </template>
        </v-data-iterator>
      </template>

      <div v-if="events.length <= 9">
        <AppShowBanners placement-name="CALENDAR" />
      </div>
      <div
        v-if="!['ganttView', 'monthView', 'adminView'].includes(calendarView)"
        class="text-right"
      >
        <slot name="load-more-btn" />
      </div>
      <GoogleMapModal v-model="mapModal" :event="selectedEvent" />
    </v-container>
  </div>
</template>

<script>
import { defineComponent, inject, ref } from "vue";
import gql from "fraql";
import { useBannerHelpers } from "@composables/banner_helpers";

import CalendarAdmin from "./CalendarAdmin/CalendarAdmin.vue";
import CalendarMonth from "./CalendarMonth.vue";
import EventCardOriginal from "@/components/event/EventCard.vue";
import EventCard from "@/components/skeletons/EventCard/EventCard.vue";
import EventCardPlaceholder from "@/components/skeletons/EventCard/EventCardPlaceholder.vue";
import EventsTemplateEvents from "@/components/table_templates/Events.vue";
import GoogleMapModal from "@elements/map/GoogleMapModal.vue";
import AppGantt from "@elements/gantt/gantt.vue";
import AppShowBanners from "@elements/banners/AppShowBanners.vue";
import AppSelectOrganisations from "@AppSelects/AppSelectOrganisations.vue";
import AppSelectProfQualification from "@AppSelects/AppSelectProfQualification.vue";
import AppGenericInput from "@AppSelects/AppGenericInput.vue";
import AppFilterNavigation from "@elements/AppFilterNavigation.vue";

import AppGenericSelect from "@AppSelects/AppGenericSelect.vue";
import AppSelectRegions from "@AppSelects/AppSelectRegions.vue";
import AppSelectDiscipline from "@AppSelects/AppSelectDiscipline.vue";
import AppSelectMultipleDates from "@AppSelects/AppSelectMultipleDates.vue";
import AppSelectPlace from "@AppSelects/AppSelectPlace.vue";
import AppSelectCups from "@AppSelects/AppSelectCups.vue";
import AppSelectCompetitionLevels from "@AppSelects/AppSelectCompetitionLevels.vue";
import AppSearchBox from "@elements/AppSearchBox.vue";
import AppSelectEventCategory from "@AppSelects/AppSelectEventCategory.vue";

import AppSelectCountries from "@AppSelects/AppSelectCountries.vue";
import AppSelectRoles from "@AppSelects/AppSelectRoles.vue";

import AppSelectPeople from "@AppSelects/AppSelectPeople.vue";

export default defineComponent({
  name: "CalendarTemplate",
  components: {
    EventCard,
    EventCardPlaceholder,
    AppShowBanners,
    AppSelectOrganisations,
    EventsTemplateEvents,
    AppFilterNavigation,
    AppGenericSelect,
    AppSelectMultipleDates,
    AppSelectPlace,
    AppSelectCups,
    AppSelectCompetitionLevels,
    AppSearchBox,
    GoogleMapModal,
    AppSelectRegions,
    AppGantt,
    AppSelectDiscipline,
    AppSelectEventCategory,
    AppSelectProfQualification,
    AppSelectCountries,
    AppGenericInput,
    CalendarMonth,
    AppSelectRoles,
    AppSelectPeople,
    CalendarAdmin
  },
  provide() {
    return {
      isNoncompetition: this.showNoncompetitions,
      registrationBtnForceClick: true
    };
  },
  props: {
    events: {
      type: Array,
      required: true
    },
    variables: {
      type: Object,
      required: true
    },
    queryVariables: {
      type: Object,
      required: true
    },
    showNoncompetitions: {
      type: Boolean,
      default: false
    },
    loadings: {
      type: Object,
      default: function () {
        return {
          eventLoading: false,
          initEventLoad: false
        };
      }
    }
  },
  emits: ["loadMore", "attachLinksNewTarget", "disableQuery"],
  setup() {
    const { showBannerConditionally } = useBannerHelpers();
    const highlightOfficialId = inject("highlightOfficialId");
    const breakdownMode = ref("week");

    function changeBreakdownMode() {
      breakdownMode.value = breakdownMode.value == "week" ? "month" : "week";
    }

    return {
      showBannerConditionally,
      changeBreakdownMode,
      breakdownMode,
      highlightOfficialId
    };
  },
  data() {
    return {
      calendarView: "normalView",
      selectedEvent: {},
      customMenuOptions: {
        closeOnContentClick: true
      },
      mapModal: false
    };
  },
  computed: {
    eventsFrom: {
      get() {
        return this.filters.eventsFrom;
      },
      set(v) {
        if (!v) {
          this.filters.eventsFrom = this.$dayjs().format("YYYY-MM-DD");
          return;
        }
        this.filters.eventsFrom = v;
      }
    },
    eventsUntil: {
      get() {
        return this.filters.eventsUntil;
      },
      set(v) {
        if (!v) {
          this.filters.eventsUntil = this.$dayjs()
            .endOf("year")
            .format("YYYY-MM-DD");
          return;
        }
        this.filters.eventsUntil = v;
      }
    },
    filters: {
      get() {
        return this.variables;
      },
      set(v) {
        this.$emit("update:variables", v);
      }
    },
    bannerPositionNumbers() {
      if (this.$vuetify.breakpoint.lgAndUp) {
        return [1, 2, 3];
      } else {
        return [1];
      }
    },
    dateOptions() {
      return [
        {
          type: "custom",
          text: this.$t(
            ["common:filters.date.lastXweeks.label", "Last {{ x }} weeks"],
            { x: 2 }
          ),
          dateFrom: this.$dayjs().subtract(14, "days").format("YYYY-MM-DD"),
          dateUntil: this.$dayjs().format("YYYY") + "-12-31"
        },
        {
          type: "custom",
          text: this.$dayjs().add(1, "years").format("YYYY"),
          dateFrom: this.$dayjs().add(1, "years").format("YYYY") + "-01-01",
          dateUntil: this.$dayjs().add(1, "years").format("YYYY") + "-12-31"
        },
        { type: "wholeYear" },
        {
          type: "custom",
          text: this.$dayjs().subtract(1, "years").format("YYYY"),
          dateFrom:
            this.$dayjs().subtract(1, "years").format("YYYY") + "-01-01",
          dateUntil:
            this.$dayjs().subtract(1, "years").format("YYYY") + "-12-31"
        }
      ];
    },
    statusesFilterOptions() {
      return [
        {
          value: "draft",
          color: "draft--text",
          text: this.$t(
            "events:event.status.Draft",
            "Draft",
            "Label for draft event status"
          )
        },
        {
          value: "proposed",
          color: "proposed--text",
          text: this.$t(
            "events:event.status.Proposed",
            "Proposed",
            "Label for proposed event status"
          )
        },
        {
          value: "published",
          color: "published--text",
          text: this.$t(
            "events:event.status.Published",
            "Published",
            "Label for published event status"
          )
        },
        {
          value: "declined",
          color: "declined--text",
          text: this.$t(
            "events:event.status.Declined",
            "Declined",
            "Label for declined event status"
          )
        },
        {
          value: "schedule_proposed",
          color: "schedule_proposed--text",
          text: this.$t(
            "events:event.status.ScheduleProposed",
            "Schedule proposed",
            "Label for schedule proposed event status"
          )
        },
        {
          value: "schedule_declined",
          color: "schedule_declined--text",
          text: this.$t(
            "events:event.status.ScheduleDeclined",
            "Schedule declined",
            "Label for schedule declined event status"
          )
        },
        {
          value: "registration_open",
          color: "registration_open--text",
          text: this.$t(
            "events:event.status.RegistrationOpen",
            "Registration open",
            "Label for registration open event status"
          )
        },
        {
          value: "registration_closed",
          color: "registration_closed--text",
          text: this.$t(
            "events:event.status.RegistrationClosed",
            "Registration closed",
            "Label for registration closed event status"
          )
        },
        {
          value: "event_running",
          color: "event_running--text",
          text: this.$t(
            "events:event.status.EventRunning",
            "LIVE",
            "Label for event running event status"
          )
        },
        {
          value: "event_ended",
          color: "event_ended--text",
          text: this.$t(
            "events:event.status.EventEnded",
            "Event ended",
            "Label for ended event status"
          )
        },
        {
          value: "results_published",
          color: "results_published--text",
          text: this.$t(
            "events:event.status.ResultsPublished",
            "Results published",
            "Label for results published event status"
          )
        },
        {
          value: "cancelled",
          color: "cancelled--text",
          text: this.$t(
            "events:event.status.Cancelled",
            "Cancelled",
            "Label for cancelled event status"
          )
        }
      ];
    },
    walletStatuses() {
      return [
        {
          value: ["OPEN"],
          text: this.$t("calendar:filters.WalletStatus.open.label", "Open")
        },
        {
          value: ["CLOSING", "CLOSED"],
          text: this.$t("calendar:filters.WalletStatus.closed.label", "Closed")
        },
        {
          value: ["NOT_USED"],
          text: this.$t(
            "calendar:filters.WalletStatus.notUsed.label",
            "Not used"
          )
        }
      ];
    },
    findEventOptions() {
      return [
        {
          value: "isHighlighted",
          text: this.$t(
            "calendar:filters.findEvent.isStar.label",
            "star events / competitions"
          )
        },
        {
          value: "hasTeamCompetitions",
          text: this.$t(
            "calendar:filters.findEvent.isTeam.label",
            "open for teams"
          )
        },
        {
          value: "hasChampionshipCompetitions",
          text: this.$t(
            "calendar:filters.findEvent.isChampionship.label",
            "championships"
          )
        }
      ];
    },
    hasEntriesOptions() {
      return [
        {
          value: "hasEntries",
          text: this.$t(
            "calendar:filters.hasEntries.hasEntries.label",
            "has entries"
          )
        },
        {
          value: "hasAcceptedEntries",
          text: this.$t(
            "calendar:filters.hasEntries.hasAcceptedEntries.label",
            "has accepted entries (FEI)"
          )
        },
        {
          value: "hasCandidacyEntries",
          text: this.$t(
            "calendar:filters.hasEntries.hasCandidacyEntries.label",
            "has candidacy entries (FEI)"
          )
        }
      ];
    },
    findQuicklyOptions() {
      return [
        {
          value: "closingSoon",
          text: this.$t(
            "calendar:filters.findQuickly.closingSoon.label",
            "closing soon for entries"
          )
        },
        {
          value: "recentlyOpened",
          text: this.$t(
            "calendar:filters.findQuickly.recentlyOpen.label",
            "Recently open for entries"
          )
        },
        {
          value: "schedulePublished",
          text: this.$t(
            "calendar:filters.findQuickly.schedulePublished.label",
            "schedule published"
          )
        },
        {
          value: "resultsPublished",
          text: this.$t(
            "calendar:filters.findQuickly.resultsPublished.label",
            "results published"
          )
        }
      ];
    }
  },
  watch: {
    calendarView(v) {
      // Disable events query if switched to monthView
      this.$emit("disableQuery", ["monthView", "adminView"].includes(v));
    }
  },
  beforeMount() {
    if (
      localStorage.getItem("calendarView") &&
      !this.$vuetify.breakpoint.mdAndDown
    ) {
      switch (localStorage.getItem("calendarView")) {
        case "normalView":
          this.normalCalendarView();
          break;
        case "compactView":
          this.compactCalendarView();
          break;
        case "ganttView":
          this.ganttCalendarView();
          break;
        case "monthView":
          if (this.showNoncompetitions) this.normalCalendarView();
          else this.monthCalendarView();
          break;
        case "adminView":
          this.adminCalendarView();
          break;
      }
    } else if (this.$vuetify.breakpoint.mdAndDown) {
      this.compactCalendarView();
    }
  },
  methods: {
    placeDetails(event) {
      this.selectedEvent = event;
      this.mapModal = true;
    },
    compactOrNormalView() {
      if (this.calendarView == "normalView") this.compactCalendarView();
      else this.normalCalendarView();
    },
    normalCalendarView() {
      this.calendarView = "normalView";

      localStorage.setItem("calendarView", "normalView");
      this.$emit("attachLinksNewTarget");
    },
    compactCalendarView() {
      this.calendarView = "compactView";

      localStorage.setItem("calendarView", "compactView");
      this.$emit("attachLinksNewTarget");
    },
    ganttCalendarView() {
      this.calendarView = "ganttView";

      localStorage.setItem("calendarView", "ganttView");
      this.$emit("attachLinksNewTarget");
    },
    monthCalendarView() {
      this.calendarView = "monthView";

      localStorage.setItem("calendarView", "monthView");
    },
    adminCalendarView() {
      this.calendarView = "adminView";

      localStorage.setItem("calendarView", "adminView");
    }
  },
  fragments: {
    events: EventsTemplateEvents.fragments.events,
    eventCard: gql`
      fragment _ on Event {
        ${EventCardOriginal.fragments.events}
        profQualificationIds
        venueAddress
        venueLocationLatitude
        venueLocationLongitude
        venueDirectionsInfo
        startingListUrl
        broadcastUrl
        homepageUrl
      }
    `
  }
});
</script>
